import { useLocation } from 'react-router-dom';
import { LibrarySidebar } from './LibrarySidebar';
import { DeveloperSidebar } from './DeveloperSidebar';
import { StoreFilterSidebar } from '@/components/store/StoreFilterSidebar';
import type { LibraryEntry, Game } from '@/types';
import type { Profile } from '@/types';

interface ConditionalSidebarWrapperProps {
  initialLibraryEntries: Array<{ entry: LibraryEntry; game: Game }>;
  profile: Profile | null;
  isDeveloper?: boolean;
}

/** Store catalog only — filter rail matches `arcadia_design/store_page`. */
function isStoreCatalogPath(pathname: string) {
  return pathname === '/store';
}

/** Product + submit + edit + reviews — full width like `arcadia_design/game_detail_page` (no library rail). */
function isStoreProductPath(pathname: string) {
  return pathname.startsWith('/store/');
}

/** Portal has its own sidebar; avoid stacking the global docs rail + portal nav. */
function isDeveloperPortalPath(pathname: string) {
  return pathname.startsWith('/developer/portal');
}

export function ConditionalSidebarWrapper({
  initialLibraryEntries,
  profile,
  isDeveloper = false,
}: ConditionalSidebarWrapperProps) {
  const { pathname } = useLocation();
  if (pathname === '/') {
    return null;
  }
  const isDeveloperPage = pathname?.startsWith('/developer') ?? false;

  if (isDeveloperPortalPath(pathname)) {
    return null;
  }

  if (isDeveloperPage && isDeveloper) {
    return <DeveloperSidebar profile={profile} />;
  }

  if (isStoreCatalogPath(pathname)) {
    return <StoreFilterSidebar />;
  }

  if (isStoreProductPath(pathname)) {
    return null;
  }

  return <LibrarySidebar initialLibraryEntries={initialLibraryEntries} profile={profile} />;
}
